import React from "react";
import { QualificationTag } from "../library/QualificationTag";

/**
 * Fallback presentation for the qualification types in `sp_programme_type`,
 * used only where a row's own icon and hue weren't loaded alongside it —
 * the filter dropdowns, which carry a type name and nothing else. Anything
 * holding the row passes its icon and hue through instead.
 */
const fallbackQualificationTypes = [
    { id: 0, name: "Short Course", icon: "fas fa-file-certificate", hue: "burnt-orange" },
    { id: 1, name: "National Certificate", icon: "fas fa-award", hue: "tide-blue" },
    { id: 2, name: "Diploma", icon: "fas fa-certificate", hue: "fresh-green" },
    { id: 3, name: "Higher Certificate", icon: "fas fa-file-certificate", hue: "naartjie" },
    { id: 4, name: "Bachelor’s Degree", icon: "fas fa-graduation-cap", hue: "burnt-orange" },
    { id: 5, name: "Honours Degree", icon: "fas fa-medal", hue: "blue" },
    { id: 6, name: "Master’s Degree", icon: "fas fa-university", hue: "plum-orchid" },
    { id: 7, name: "Learning Path", icon: "fas fa-route", hue: "tide-blue" },
    { id: 8, name: "Occupational Certificate", icon: "fas fa-user-graduate", hue: "grey" },
];

const qualificationTypesById = new Map(
    fallbackQualificationTypes.map((qualificationType) => [qualificationType.id, qualificationType])
);

const qualificationTypesByName = new Map(
    fallbackQualificationTypes.map((qualificationType) => [
        qualificationType.name.toLowerCase().replace(/’/g, "'"),
        qualificationType,
    ])
);

/**
 * Looks a qualification type up in the fallback list, by the name the API
 * returns or, where a programme has no matching `sp_programme_type` row, by the
 * raw id it falls back to.
 * @param {string | number} degree Qualification type name or sp_programme_type id
 * @returns The matching type, or undefined
 */
function findFallbackType(degree) {
    if (degree === null || degree === undefined || degree === "") {
        return undefined;
    }

    if (typeof degree === "number") {
        return qualificationTypesById.get(degree);
    }

    const value = String(degree).trim();

    if (/^\d+$/.test(value)) {
        return qualificationTypesById.get(Number(value));
    }

    return qualificationTypesByName.get(value.toLowerCase().replace(/’/g, "'"));
}

/**
 * Works out what to draw for a qualification type, preferring the icon and
 * hue the row carries over the fallback list.
 * @param {string | number} degree Qualification type name or sp_programme_type id
 * @param {string} icon Icon class from sp_programme_type.icon
 * @param {string} hue Brand palette name from sp_programme_type.hue
 * @returns The name, icon and hue to render, or undefined when unknown
 */
function resolveQualification(degree, icon, hue) {
    const fallbackType = findFallbackType(degree);
    const trimmedDegree = typeof degree === "string" ? degree.trim() : "";
    const name = trimmedDegree && !/^\d+$/.test(trimmedDegree) ? trimmedDegree : fallbackType && fallbackType.name;

    if (!name) {
        return undefined;
    }

    return {
        name,
        icon: icon || (fallbackType && fallbackType.icon),
        hue: hue || (fallbackType && fallbackType.hue),
    };
}

/**
 * The qualification type as a tinted pill with its icon.
 * @param {object} props degree - type name or id; icon and hue - from the row, when it has them
 */
function CommonCourseComponents({ degree, icon, hue }) {
    const qualification = resolveQualification(degree, icon, hue);

    if (!qualification) {
        return (
            <QualificationTag $hue="grey">N/A</QualificationTag>
        );
    }

    return (
        <QualificationTag $hue={qualification.hue} title={qualification.name}>
            {qualification.icon && <i className={qualification.icon} />}
            {qualification.name}
        </QualificationTag>
    );
}

/**
 * Just the qualification type's icon, for places too tight for the full pill.
 * @param {object} props degree - type name or id; icon - from the row, when it has one
 */
export function ProgramIcons({ degree, icon }) {
    const qualification = resolveQualification(degree, icon);

    if (!qualification || !qualification.icon) {
        return null;
    }

    return (
        <i className={qualification.icon} title={qualification.name} />
    );
}

/**
 * The qualification type's plain name, for exports and filters.
 * @param {string | number} degree Qualification type name or sp_programme_type id
 * @returns Display name, or a dash
 */
export function qualificationLabel(degree) {
    const qualification = resolveQualification(degree);

    return qualification ? qualification.name : "-";
}

export default CommonCourseComponents;
